
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../LoginModal';
import RegisterModal from '../RegisterModal';
import LanguageSwitcher from '../LanguageSwitcher';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const { user, isAuthenticated, logout, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const navbarClasses = isHomePage && !isScrolled
    ? 'bg-transparent text-white'
    : 'bg-white text-gray-900 shadow-sm';

  const linkClasses = isHomePage && !isScrolled
    ? 'text-white/90 hover:text-white text-sm font-medium tracking-wide'
    : 'text-gray-700 hover:text-[#1A2B4A] text-sm font-medium tracking-wide';

  const buttonOutlineClasses = isHomePage && !isScrolled
    ? 'border border-white/60 text-white hover:bg-white/10'
    : 'border border-gray-300 text-gray-700 hover:bg-gray-50';

  const buttonSolidClasses = isHomePage && !isScrolled
    ? 'bg-white text-[#1A2B4A] hover:bg-white/90'
    : 'bg-[#1A2B4A] text-white hover:bg-[#2A3B5A]';

  const logoTextClass = isHomePage && !isScrolled ? 'text-white' : 'text-[#1A2B4A]';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navbarClasses}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="flex flex-col leading-none">
                <span className={`text-xl font-bold tracking-[0.15em] uppercase ${logoTextClass} transition-colors`}
                  style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.18em' }}>
                  BN ĀGENZIA
                </span>
                <span className={`text-[9px] tracking-[0.35em] uppercase font-medium mt-0.5 ${isHomePage && !isScrolled ? 'text-white/60' : 'text-gray-400'}`}>
                  International Placement
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-7">
              <a href={isHomePage ? '#about' : '/#about'} className={`${linkClasses} transition-colors whitespace-nowrap`}>
                {t('nav_about')}
              </a>
              <a href={isHomePage ? '#services' : '/#services'} className={`${linkClasses} transition-colors whitespace-nowrap`}>
                {t('nav_services')}
              </a>
              <a href={isHomePage ? '#method' : '/#method'} className={`${linkClasses} transition-colors whitespace-nowrap`}>
                {t('nav_method')}
              </a>
              <Link to="/biblioteca-estrategica" className={`${linkClasses} transition-colors whitespace-nowrap`}>
                {t('nav_library', 'Biblioteca')}
              </Link>
              <a href={isHomePage ? '#contact' : '/#contact'} className={`${linkClasses} transition-colors whitespace-nowrap`}>
                {t('nav_contact')}
              </a>

              <LanguageSwitcher />

              {/* Auth buttons hidden */}
              {isAuthenticated && user && (
                <div className="flex items-center space-x-3">
                  <Link
                    to={user.userType === 'admin' ? '/admin' : '/perfil'}
                    className={`${linkClasses} transition-colors whitespace-nowrap`}
                  >
                    {user.userType === 'admin' ? 'Panel Admin' : t('nav_my_profile')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${buttonOutlineClasses}`}
                  >
                    {t('nav_logout')}
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${isHomePage && !isScrolled ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              aria-label="Toggle menu"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-6 space-y-1">
              <a
                href={isHomePage ? '#about' : '/#about'}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 px-2 text-gray-700 hover:text-[#1A2B4A] text-sm font-medium transition-colors border-b border-gray-50"
              >
                {t('nav_about')}
              </a>
              <a
                href={isHomePage ? '#services' : '/#services'}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 px-2 text-gray-700 hover:text-[#1A2B4A] text-sm font-medium transition-colors border-b border-gray-50"
              >
                {t('nav_services')}
              </a>
              <a
                href={isHomePage ? '#method' : '/#method'}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 px-2 text-gray-700 hover:text-[#1A2B4A] text-sm font-medium transition-colors border-b border-gray-50"
              >
                {t('nav_method')}
              </a>
              <Link
                to="/biblioteca-estrategica"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 px-2 text-gray-700 hover:text-[#1A2B4A] text-sm font-medium transition-colors border-b border-gray-50"
              >
                {t('nav_library', 'Biblioteca')}
              </Link>
              <a
                href={isHomePage ? '#contact' : '/#contact'}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 px-2 text-gray-700 hover:text-[#1A2B4A] text-sm font-medium transition-colors border-b border-gray-50"
              >
                {t('nav_contact')}
              </a>

              <div className="pt-4 pb-2">
                <LanguageSwitcher />
              </div>

              {isAuthenticated && user && (
                <div className="pt-2 space-y-3">
                  <Link
                    to={user.userType === 'admin' ? '/admin' : '/perfil'}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full py-2.5 px-4 text-center bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    {user.userType === 'admin' ? 'Panel Admin' : t('nav_my_profile')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full py-2.5 px-4 text-center border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    {t('nav_logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <RegisterModal isOpen={showRegisterModal} onClose={() => setShowRegisterModal(false)} />
    </>
  );
}
